import type {
  PortalBrandTheme,
  PortalReportConfig,
  PortalReportLabels,
  PortalReportSectionId,
} from '../../types/portalReport.js';
import { AF_AU, AF_UK } from '../clubwise/brands.js';
import {
  brandSlugFromName,
  PORTAL_BRAND_THEMES,
  type PortalBrandSlug,
} from './brandThemes.js';

// CLIENT PORTAL Phase 1 — per-brand report composition.
//
// Scott: *"the report for each Brand will be slightly different and connected to
// different BigQuery sources for CRM data."*
//
// ⚠️⚠️ THIS INDIRECTION EXISTS NOW SO THE CRM PHASE IS NOT A SWEEP. v1 needs
// exactly one label set and one section order, which is precisely why it would
// be tempting to hardcode "Leads" into eight components and a section order into
// three pages. Doing that costs nothing today and costs a rewrite the moment a
// dealership's report needs to say "sales" where a gym's says "members" — the
// change would land in every component at once, which is how a label ends up
// wrong on one chart and right on the next.
//
// So: every component reads its wording from `labels`, every page reads its
// layout from a `sections` list, and adding a brand is ONE registry entry.
//
// ⚠️ The registry is CODE for v1, deliberately — moving it to Firestore before
// there is a second entry would be building an admin surface for a thing nobody
// has asked to edit yet. The shape is what matters; where it is stored is a
// later, cheap change.

export const DEFAULT_PORTAL_LABELS: PortalReportLabels = {
  // ⚠️⚠️ "AD CONVERSIONS", NOT "LEADS" — settled by Scott, 2026-09-03. This is
  // `SUM(conversions)` on Google and Meta's `leads`: the AD PLATFORMS' OWN
  // conversion counts, not the enquiries the club recorded. The CRM panel on the
  // same page counts 262 where this counts 33, and calling both of them "leads"
  // is the confusion this rename exists to remove.
  //
  // ⚠️ EVERYWHERE. The hero, the channel pages, the chart metric switches and
  // the leaderboard all read this field, which is what makes one number carry
  // one name across the whole report — a component that hardcoded "Leads" at its
  // call site would put the old word back on one surface only.
  leads: 'Ad Conversions',
  leadsSingular: 'Ad Conversion',
  // ⚠️ `Ad Conv.` is permitted where a column is genuinely too narrow. It is
  // DRIVEN FROM HERE rather than abbreviated at the call site, so a surface
  // cannot end up using the short form in one column and the long one in the
  // next.
  leadsShort: 'Ad Conv.',
  costPerLead: 'Cost per lead',
  spend: 'Ad spend',
  // The CRM vocabulary. Snap says "sales"; a brand that counts "members" or
  // "signups" overrides these four rather than every component being edited.
  sales: 'Sales',
  salesSingular: 'Sale',
  costPerSale: 'Cost per sale',
  conversionRate: 'Conversion rate',
  // ⚠️⚠️ "TOTAL LEADS" — SCOTT'S CLIENTS' WORD (2026-09-03), and its own
  // entry rather than a composition of `leads` above. See the field's note in
  // portalReport.ts for the number it names and for why composing it was the
  // bug: with `leads: 'Ad Conversions'`, `Total ${leads}` printed the club's
  // whole enquiry count under the ad platforms' name.
  //
  // ⚠️ WHAT KEEPS IT SAFE IS STRUCTURAL RATHER THAN LEXICAL: it sits BESIDE
  // "Internet leads" with the nesting sentence above the row, so the
  // relationship is visible in the layout. The word alone never carried it.
  crmTotalLeads: 'Total leads',
};

/**
 * The v1 section orders. ⚠️ `crm` is deliberately ABSENT from all three: it is
 * in the vocabulary so a renderer and a config entry are all the CRM phase needs,
 * and absent so nothing renders an empty shell today. A section id with no
 * renderer is skipped rather than crashing the page — see the page components.
 */
const DEFAULT_OVERVIEW_SECTIONS: PortalReportSectionId[] = [
  'combined',
  'google',
  'meta',
  'byLocation',
];

const DEFAULT_META_SECTIONS: PortalReportSectionId[] = [
  'meta',
  'byLocation',
  'campaigns',
  'creatives',
];

const DEFAULT_GOOGLE_SECTIONS: PortalReportSectionId[] = [
  'google',
  'byLocation',
  'campaigns',
  'searchTerms',
];

/**
 * ⚠️⚠️ BRAND ACCOUNT ID → THEME SLUG. THIS IS THE ONE PLACE THAT NEEDS REAL IDS,
 * and it ships EMPTY because it cannot be filled in from outside the database:
 * account ids are Firestore auto-ids (`collection('accounts').doc()`), so there
 * is no id for Snap Fitness Australia that can be derived, guessed or computed
 * here. Inventing one would register a theme against an account that does not
 * exist — which looks exactly like the feature being broken.
 *
 * ⚠️ FILLED IN 2026-08-22 from `ad_performance.account_dimensions` — the Firestore
 * mirror — so these are live brand-account doc ids, each verified as
 * `account_type = 'brand'`. Source of truth if they ever need re-deriving:
 *
 *   SELECT DISTINCT brand_account_id, brand_name
 *   FROM `kyck-agency-prod.ad_performance.account_dimensions`
 *   WHERE brand_account_id IS NOT NULL
 *
 * ⚠️⚠️ THERE ARE THREE SNAP BRAND ACCOUNTS, NOT TWO. Singapore is small (5
 * accounts against Australia's 110) and is exactly the one a manual pass misses.
 * All three point at the same theme.
 *
 * `brandSlugFromName` stays as the fallback for a brand whose id is not listed
 * here, and an entry WINS over it — so this is strictly a tightening. It is also
 * now VERIFIED to work rather than assumed: all four names above satisfy its
 * rules, so the two paths agree today.
 */
export const BRAND_ACCOUNT_THEMES: Record<string, PortalBrandSlug> = {
  // Fitstop — 128 accounts
  XxzU3Dc6VHaSAjgmdRo0: 'fitstop',
  // Snap Fitness Australia — 110 accounts
  suFWmFLUtY9HHfdMtpif: 'snap-fitness',
  // Snap Fitness New Zealand — 57 accounts
  is42xEM1iQuM17jOWrJg: 'snap-fitness',
  // Snap Fitness Singapore — 5 accounts
  LPPoInMO4a1e6tsUa61f: 'snap-fitness',
  // Jetts Australia — 1 account (Lutwyche). ⚠️ Read from Firestore directly
  // rather than from `account_dimensions`, and verified as `accountType: 'brand'`.
  dlZOfJZAzPjlsrDpxWAT: 'jetts',
  // O-Studio — ⚠️ ID NOT YET SUPPLIED (2026-09-25). It is the last segment of
  // the brand account page's URL (the account where `portalMetaGoalReport` is
  // ticked). Until it is filled in, `brandSlugFromName` binds O-Studio by name.
  // Do not guess an id: a wrong one binds nothing and looks like a broken theme.
  // <brand-account-id>: 'o-studio',
};

/**
 * Per-brand overrides, keyed by the BRAND account id (resolved through the same
 * `computeBrandAncestorMap` every other brand surface uses, so a dealership
 * resolves to its marque and a franchise location to its brand — one definition
 * of "which brand is this", not a second one here).
 *
 * ⚠️ STILL EMPTY, and still deliberately so. This registry carries LABEL and
 * SECTION overrides, and neither Snap nor Fitstop wants different words or a
 * different section order — v1 needs "Leads" for both. A fabricated entry here
 * would be a fake brand in a production path. The THEME binding is separate
 * (`BRAND_ACCOUNT_THEMES` above) precisely because the two answer different
 * questions and become non-empty at different times.
 */
/**
 * ⚠️⚠️ SNAP FITNESS' GYMSALES TABLES, AND THE ONE THAT MUST NEVER BE READ.
 *
 * `snapaugymsales.gymsales` is a table SET, not a table, and the country choice
 * is real: AU and NZ are separate tables with slightly different columns (NZ v2
 * carries `sale_date` + `sale_source`, and `calls_count` is INTEGER there and
 * STRING in AU — which is on its own why nothing may `SELECT *`).
 *
 * ⚠️⚠️ `gymsales_snap_fitness_nz` IS DEAD AND IT OVERLAPS `_v2`. Measured
 * 2026-08-31: its last row is 2024-09-16 while `_v2` starts 2024-01-01, so ANY
 * union of the two DOUBLE-COUNTS January–September 2024. Read `_v2` only. The
 * dead name appears nowhere in this codebase and `deadNzTable.test.ts` asserts
 * that on the source, because a plausible-looking "let's include the history"
 * edit would silently double nine months of a client's leads.
 *
 * ⚠️ Keyed by the ACCOUNT'S OWN COUNTRY, not by which kyck brand account it
 * hangs under. GymSales splits by country; kyck's brand accounts happen to as
 * well, but a club filed under the AU brand with a New Zealand country must
 * still read the NZ table — so the country decides and the same map is
 * registered under every Snap brand account.
 *
 * ⚠️ SINGAPORE IS ABSENT DELIBERATELY. `gymsales_snap_fitness_sg` exists in the
 * dataset; the scope Scott locked is AU and NZ. A Singapore club therefore
 * resolves to NO CRM source and its panel is OMITTED rather than emptied — which
 * is the honest outcome, and it falls out of the map rather than needing a
 * check.
 */
const SNAP_GYMSALES_BY_COUNTRY: Record<string, PortalCrmSource> = {
  Australia: {
    projectId: 'snapaugymsales',
    dataset: 'gymsales',
    table: 'gymsales_snap_fitness_au',
  },
  'New Zealand': {
    projectId: 'snapaugymsales',
    dataset: 'gymsales',
    table: 'gymsales_snap_fitness_nz_v2',
  },
};

/**
 * Per-brand overrides, keyed by the BRAND account id.
 *
 * ⚠️ It stopped being empty on 2026-08-31, and only for CRM. Neither Snap nor
 * Fitstop wants different WORDS or a different section ORDER, so `labels` and
 * the section lists are still absent from every entry — the entries below carry
 * a data-source binding and nothing else.
 */
/**
 * ⚠️ The Overview order for a brand with a CRM, and the ONLY reason any brand
 * overrides a section list today: `crm` sits second, straight after the combined
 * block. The hero counts the leads the ads produced; the CRM panel is what the
 * club did with every enquiry it received, and burying that under the campaign
 * tables would put the mechanism above the outcome.
 *
 * ⚠️ It governs the PRE-RESTYLE report only — the restyled one renders its own
 * band order. A section id with no renderer is skipped, so this is safe on any
 * page that has not got one.
 */
const CRM_OVERVIEW_SECTIONS: PortalReportSectionId[] = [
  'combined',
  'crm',
  'google',
  'meta',
  'byLocation',
];

/**
 * Where Snap's CLUB LIST lives — the same two tables as the facts, because the
 * Snap tables carry club attributes on every fact row. ⚠️ Stated explicitly
 * rather than inferred from `SNAP_GYMSALES_BY_COUNTRY`: that two questions have
 * one answer here is a fact about these tables, not a rule every brand obeys.
 * See `PortalCrmClubSource`.
 */
const SNAP_GYMSALES_COLUMNS: PortalCrmClubColumns = {
  crmId: 'company_id',
  name: 'club_name',
  country: 'country',
  state: 'state',
};

const SNAP_CLUBS_BY_COUNTRY: Record<string, PortalCrmClubSource> = {
  Australia: {
    projectId: 'snapaugymsales',
    dataset: 'gymsales',
    table: 'gymsales_snap_fitness_au',
    columns: SNAP_GYMSALES_COLUMNS,
    filter: null,
  },
  'New Zealand': {
    projectId: 'snapaugymsales',
    dataset: 'gymsales',
    table: 'gymsales_snap_fitness_nz_v2',
    columns: SNAP_GYMSALES_COLUMNS,
    filter: null,
  },
};

const SNAP_OVERRIDE: PortalBrandOverride = {
  crmByCountry: SNAP_GYMSALES_BY_COUNTRY,
  crmClubsByCountry: SNAP_CLUBS_BY_COUNTRY,
  overviewSections: CRM_OVERVIEW_SECTIONS,
};

/**
 * ⚠️⚠️ JETTS READS OUR OWN DATASET, NOT THE CLIENT'S — the opposite of Snap, and
 * the difference is worth understanding before copying either.
 *
 * Snap's figures land in `snapaugymsales`, a project the CLIENT owns, and
 * `crmReporting` reads them in place. Jetts has no such landing: kyck runs the
 * GymSales loader (`crmSync`), so the facts are ours, in `crm_data`, co-located
 * with `ad_performance`.
 *
 * ⚠️ IT POINTS AT A VIEW, which is the whole reason `PortalCrmSource.table` is a
 * descriptor rather than a table name spelled into a query.
 * `v_crm_reporting_facts` reassembles the flag-column shape `crmQuery` reads
 * from the two cleanly-split fact tables, applies `crm_clubs.data_valid_from`,
 * and projects no PII — so it is also the authorised view `crmQuery.ts`'s header
 * has been asking for. Adopting it was this one entry.
 *
 * ⚠️ ONE COUNTRY, because Jetts Australia is one brand account with Australian
 * clubs. An account whose country is not in this map resolves to NO CRM source
 * and its panel is OMITTED rather than emptied — the same honest outcome
 * Singapore gets on the Snap side.
 */
const JETTS_CRM_BY_COUNTRY: Record<string, PortalCrmSource> = {
  Australia: {
    projectId: 'kyck-agency-prod',
    dataset: 'crm_data',
    table: 'v_crm_reporting_facts',
  },
};

/**
 * ⚠️⚠️ JETTS' CLUBS ARE NOT IN ITS FACTS VIEW. `v_crm_reporting_facts` is a
 * facts view and projects no club name; the club list is the `crmSync` registry
 * table `crm_clubs`, whose name column is `name` and whose country is a GymSales
 * code (AU), reconciled downstream by `canonicalCountry`. Reading the club list
 * from the facts descriptor is what failed `backfill:crm-id` with
 * "Unrecognized name: club_name" (2026-09-28).
 *
 * ⚠️ FILTERED TO THIS BRAND'S ROWS: `crm_clubs` is shared by every brand
 * `crmSync` loads, keyed by the crmSync brand key (server/modules/crmSync/config.ts).
 */
const JETTS_CLUBS_BY_COUNTRY: Record<string, PortalCrmClubSource> = {
  Australia: {
    projectId: 'kyck-agency-prod',
    dataset: 'crm_data',
    table: 'crm_clubs',
    columns: { crmId: 'company_id', name: 'name', country: 'country', state: 'state' },
    filter: { column: 'brand', equals: 'jetts' },
  },
};

const JETTS_OVERRIDE: PortalBrandOverride = {
  crmByCountry: JETTS_CRM_BY_COUNTRY,
  crmClubsByCountry: JETTS_CLUBS_BY_COUNTRY,
  // ⚠️ INERT ON THE RESTYLED REPORT, which renders its own band order and is what
  // a Jetts portal user actually gets (`restyledReportEnabled` returns true for
  // any non-empty scope). Carried anyway so the pre-restyle path — still the
  // documented revert — puts the CRM panel straight after the combined block
  // rather than under the campaign tables.
  overviewSections: CRM_OVERVIEW_SECTIONS,
};

/**
 * ⚠️⚠️ ANYTIME FITNESS READS CLUBWISE UPLOADS, in OUR dataset (like Jetts).
 *
 * ClubWise has no API: Kyck staff upload two CSV exports per club per month
 * (server/modules/clubwise). The view carries the SAME six columns as
 * `v_crm_reporting_facts`, so `crmQuery` reads it unchanged.
 *
 * ⚠️ `clubKey: 'clubwiseCode'` — the club key is `Account.clubwiseCode`, NOT
 * `crmId` (GymSales; read by the Source Health grid and CRM health panels).
 * ⚠️ `leadGrain: 'monthly'` — ClubWise leads have no date, only an upload month.
 * ⚠️ ONE COUNTRY: AF is AU only for now.
 */
const CLUBWISE_CRM_BY_COUNTRY: Record<string, PortalCrmSource> = {
  Australia: {
    projectId: 'kyck-agency-prod',
    dataset: 'crm_data',
    table: 'v_clubwise_reporting_facts',
    clubKey: 'clubwiseCode',
    leadGrain: 'monthly',
    provenanceLabel: 'ClubWise (monthly upload)',
    brandKey: AF_AU.brandKey,
  },
};

/**
 * ⚠️ ANYTIME FITNESS UK: its OWN view (`v_clubwise_uk_reporting_facts`, brand-filtered
 * in SQL), so AU's view, its queries and its numbers are untouched. The country key is
 * what UK accounts store: 'United Kingdom'.
 */
const CLUBWISE_UK_CRM_BY_COUNTRY: Record<string, PortalCrmSource> = {
  [AF_UK.accountCountry]: {
    projectId: 'kyck-agency-prod',
    dataset: 'crm_data',
    table: 'v_clubwise_uk_reporting_facts',
    clubKey: 'clubwiseCode',
    leadGrain: 'monthly',
    provenanceLabel: 'ClubWise (monthly upload)',
    brandKey: AF_UK.brandKey,
  },
};

/** ⚠️ `name` is the club code: ClubWise uploads carry no club name. */
const CLUBWISE_CLUBS_BY_COUNTRY: Record<string, PortalCrmClubSource> = {
  Australia: {
    projectId: 'kyck-agency-prod',
    dataset: 'crm_data',
    table: 'clubwise_clubs',
    columns: { crmId: 'company_id', name: 'name', country: 'country', state: 'state' },
    filter: null,
    clubKey: 'clubwiseCode',
    leadGrain: 'monthly',
    brandKey: AF_AU.brandKey,
  },
};

const CLUBWISE_UK_CLUBS_BY_COUNTRY: Record<string, PortalCrmClubSource> = {
  [AF_UK.accountCountry]: {
    projectId: 'kyck-agency-prod',
    dataset: 'crm_data',
    table: 'clubwise_uk_clubs',
    columns: { crmId: 'company_id', name: 'name', country: 'country', state: 'state' },
    filter: null,
    clubKey: 'clubwiseCode',
    leadGrain: 'monthly',
    brandKey: AF_UK.brandKey,
  },
};

const CLUBWISE_OVERRIDE: PortalBrandOverride = {
  // ⚠️ AF's digital definition is wider than an online form (Contact Type OR
  // Source), so "Internet" would overclaim. Snap and Jetts keep their words.
  labels: { digitalLeads: 'Digital leads', costPerDigitalLead: 'Cost per digital lead' },
  crmByCountry: CLUBWISE_CRM_BY_COUNTRY,
  crmClubsByCountry: CLUBWISE_CLUBS_BY_COUNTRY,
  overviewSections: CRM_OVERVIEW_SECTIONS,
};

/** Same labels and sections as AU; GBP comes from the UK accounts' own ad accounts (money is never converted). */
const CLUBWISE_UK_OVERRIDE: PortalBrandOverride = {
  ...CLUBWISE_OVERRIDE,
  crmByCountry: CLUBWISE_UK_CRM_BY_COUNTRY,
  crmClubsByCountry: CLUBWISE_UK_CLUBS_BY_COUNTRY,
};

export const PORTAL_BRAND_CONFIGS: Record<string, PortalBrandOverride> = {
  // Anytime Fitness Australia — ClubWise uploads.
  // ⚠️ NOT CONFIRMED FROM THIS REPO that this id is the Anytime Fitness BRAND
  // account: the id appears nowhere else in the codebase (Snap's and Jetts' are
  // also in STATE.md and tests). It was supplied by Scott (2026-10-17) and is the
  // last segment of the brand's /accounts/<id> URL. Verify before the first
  // report is opened.
  rBEU09v5W7cJ861X3GA3: CLUBWISE_OVERRIDE,
  // Anytime Fitness United Kingdom — ClubWise uploads (brand account id from Scott, 2026-10-30).
  iUZTT9GkDYfHHEo3Q573: CLUBWISE_UK_OVERRIDE,
  // Snap Fitness Australia
  suFWmFLUtY9HHfdMtpif: SNAP_OVERRIDE,
  // Snap Fitness New Zealand
  is42xEM1iQuM17jOWrJg: SNAP_OVERRIDE,
  // Snap Fitness Singapore — registered so an AU/NZ club mis-filed under this
  // brand still resolves; Singapore itself is not in the country map, so an
  // actual Singapore club resolves to no CRM source and the section, having no
  // block to render, is skipped. See the note above.
  LPPoInMO4a1e6tsUa61f: SNAP_OVERRIDE,
  // Jetts Australia — one club today (Lutwyche, GymSales company 4587)
  dlZOfJZAzPjlsrDpxWAT: JETTS_OVERRIDE,
};

/**
 * Where a brand's CRM figures live: a fully-qualified BigQuery table, read in
 * place rather than pulled into our own dataset.
 *
 * ⚠️ `table` may name a VIEW as easily as a table, and it should: the AU source
 * carries PII in `person_name` (397,509 distinct lead names), and the structural
 * fix is an authorised view in the client's project that excludes that column,
 * with the reporting service account granted access to the view alone.
 * ⚠️ `contact` was named here as PII until 2026-09-15 and is NOT — it is an
 * 11-value enquiry-channel enum. See `crmQuery.ts`'s header for the measurement. Pointing at one is
 * a one-line edit here — which is the reason this is a descriptor rather than a
 * table name spelled into a query.
 */
export interface PortalCrmSource {
  projectId: string;
  dataset: string;
  table: string;
  /**
   * ⚠️ WHICH ACCOUNT FIELD IS THE CLUB KEY for this source. Absent = `crmId`
   * (GymSales: Snap, Jetts). ClubWise (Anytime Fitness) uses `clubwiseCode`.
   * Chosen by the BRAND'S SOURCE, never by `crmId ?? clubwiseCode`: a fallback
   * would let a stray GymSales id on an AF account be read as a ClubWise code
   * (and vice versa) and report another club's figures under this one's name.
   */
  clubKey?: 'crmId' | 'clubwiseCode';
  /**
   * ⚠️ HOW OLD A LEAD CAN BE, AT BEST. Absent = `daily` (GymSales). `monthly`
   * means leads carry only the MONTH they were uploaded for (ClubWise New
   * Prospects has no lead date), so lead figures cover whole uploaded months
   * only and a daily view shows leads as a gap. Sales stay daily.
   */
  leadGrain?: 'daily' | 'monthly';
  /**
   * ⚠️ WHAT THE REPORT'S "Where these figures came from" FOOTER CALLS THIS CRM.
   * Absent = `GymSales` (Snap, Jetts), so their output is unchanged.
   */
  provenanceLabel?: string;
  /**
   * ⚠️⚠️ CLUBWISE ONLY: the BRAND KEY that scopes every read of the ClubWise detail
   * tables (`clubwise_leads` / `_joins` / `_uploads`) alongside the club code
   * (src/lib/clubwise/brands.ts). The facts VIEW is already one per brand; the
   * detail tables are shared, so without this an AU and a UK club with the same code
   * would read each other. Absent for GymSales sources.
   */
  brandKey?: string;
}

/** The footer's name for a source's CRM. */
export function provenanceLabelOf(source: PortalCrmSource): string {
  return source.provenanceLabel ?? 'GymSales';
}

/** The account field that is this source's club key. */
export function clubKeyFieldOf(source: PortalCrmSource): 'crmId' | 'clubwiseCode' {
  return source.clubKey ?? 'crmId';
}

/** Is a source's lead figure monthly grain? */
export function isMonthlyLeadGrain(source: PortalCrmSource): boolean {
  return source.leadGrain === 'monthly';
}

/** Which column in a club-list table holds each attribute the matcher needs. */
export interface PortalCrmClubColumns {
  crmId: string;
  name: string;
  country: string;
  state: string;
}

/**
 * Where a brand's CLUB LIST lives — the rows an account's `crmId` is matched
 * against. ⚠️⚠️ A SEPARATE QUESTION FROM `PortalCrmSource`, which answers
 * "where are the figures". For Snap both answers are one table; for Jetts the
 * figures are a view and the clubs a registry table, and reusing the facts
 * descriptor for the club list is the defect this type exists to prevent
 * (_architect/findings/2026-09-28-crm-id-backfill-jetts-schema.md).
 *
 * ⚠️ It carries its COLUMNS because the registry is only authoritative for a
 * schema it states. `crmClubList.test.ts` checks every entry's query against
 * the table's real column set, deny-by-default.
 */
export interface PortalCrmClubSource extends PortalCrmSource {
  columns: PortalCrmClubColumns;
  /** Restrict to one brand's rows in a table several brands share. */
  filter: { column: string; equals: string } | null;
}

export interface PortalBrandOverride {
  labels?: Partial<PortalReportLabels>;
  overviewSections?: PortalReportSectionId[];
  metaSections?: PortalReportSectionId[];
  googleSections?: PortalReportSectionId[];
  /**
   * ⚠️ An INLINE theme, for a brand whose theme is not one of the shared slugs.
   * Registered themes are the normal path; this exists so the override shape is
   * complete rather than because anything uses it today.
   */
  theme?: PortalBrandTheme;
  /**
   * ⚠️⚠️ THE CRM TABLE BINDING LIVES ON THE BRAND, NOT THE ACCOUNT. Scott: *"the
   * brand template will now be more about the template layout & the CRM
   * integration."* Storing a table name on each of 330 accounts would be 330
   * places for it to be wrong, and the `_nz` → `_nz_v2` cutover — exactly the
   * kind of change this will see — would then be a migration instead of a
   * one-line registry edit.
   *
   * Keyed by the account's own COUNTRY. See `SNAP_GYMSALES_BY_COUNTRY`.
   */
  crmByCountry?: Record<string, PortalCrmSource>;
  /**
   * Where the brand's club list lives, per country. ⚠️ Every country in
   * `crmByCountry` must appear here too — asserted in `crmClubList.test.ts`, so
   * a brand cannot gain CRM figures without saying where its clubs are.
   */
  crmClubsByCountry?: Record<string, PortalCrmClubSource>;
}

/** Case- and whitespace-insensitive country lookup shared by both resolvers. */
function byCountry<T>(map: Record<string, T> | undefined, country: string): T | null {
  if (!map) return null;
  const wanted = country.trim().toLowerCase();
  for (const [name, value] of Object.entries(map)) {
    if (name.trim().toLowerCase() === wanted) return value;
  }
  return null;
}

/**
 * Which CRM table answers for THIS account, or null.
 *
 * ⚠️⚠️ NULL IS A FIRST-CLASS ANSWER, and it is the common one: a brand with no
 * CRM descriptor, an account whose country is not in the brand's map, or no
 * brand at all. The report OMITS the CRM panel in that case rather than
 * rendering it empty — the same posture as every other absent section, and the
 * one that keeps "we don't have this" from reading as "you had none".
 *
 * ⚠️ Country is matched case- and whitespace-insensitively. The stored value is
 * canonical ("Australia" / "New Zealand" — the AccountForm dropdown and the
 * currency seed agree on the spelling), so this is a tolerance rather than a
 * guess: it can only rescue a hand-typed value, never pick a different country.
 */
export function resolvePortalCrmSource(
  brandAccountId: string | null,
  country: string | null,
  registry: Record<string, PortalBrandOverride> = PORTAL_BRAND_CONFIGS,
): PortalCrmSource | null {
  if (!brandAccountId || !country) return null;
  return byCountry(registry[brandAccountId]?.crmByCountry, country);
}

/**
 * Which CLUB-LIST table answers for this account, or null. Same country
 * matching as `resolvePortalCrmSource`, reading `crmClubsByCountry`.
 */
export function resolvePortalCrmClubSource(
  brandAccountId: string | null,
  country: string | null,
  registry: Record<string, PortalBrandOverride> = PORTAL_BRAND_CONFIGS,
): PortalCrmClubSource | null {
  if (!brandAccountId || !country) return null;
  return byCountry(registry[brandAccountId]?.crmClubsByCountry, country);
}

/**
 * Resolve a brand's theme.
 *
 * Precedence, and each step is deliberate:
 *   1. an INLINE `theme` on the brand's override — the most specific statement;
 *   2. the id → slug binding, which is exact;
 *   3. the NAME match, the documented fallback while ids are unknown;
 *   4. null — kyck's own look.
 *
 * ⚠️⚠️ AN UNKNOWN BRAND RESOLVES TO null, NEVER TO THE FIRST REGISTERED THEME.
 * That fallback would put Snap's red on a Fitstop report, and it is the kind of
 * mistake that looks like a deliberate design choice to whoever sees it.
 */
export function resolveBrandTheme(
  brandAccountId: string | null,
  brandName: string | null = null,
  registry: Record<string, PortalBrandOverride> = PORTAL_BRAND_CONFIGS,
  bindings: Record<string, PortalBrandSlug> = BRAND_ACCOUNT_THEMES,
  themes: Record<string, PortalBrandTheme> = PORTAL_BRAND_THEMES,
): PortalBrandTheme | null {
  const inline = brandAccountId ? registry[brandAccountId]?.theme : undefined;
  if (inline) return inline;
  const slug = (brandAccountId ? bindings[brandAccountId] : undefined) ?? brandSlugFromName(brandName);
  return (slug ? themes[slug] : undefined) ?? null;
}

/**
 * Resolve the report config for a brand.
 *
 * ⚠️ An unknown brand — or none at all, which is the normal case for an account
 * outside a brand tree — resolves to the DEFAULT rather than failing. A missing
 * config must never be the reason a client cannot see their report.
 *
 * Overrides are merged FIELD BY FIELD, so a brand that wants only a different
 * word for "leads" supplies one key and inherits everything else. A brand
 * supplying a partial label set cannot accidentally blank the others.
 */
export function resolvePortalReportConfig(
  brandAccountId: string | null,
  brandName: string | null = null,
  registry: Record<string, PortalBrandOverride> = PORTAL_BRAND_CONFIGS,
): PortalReportConfig {
  const override = brandAccountId ? registry[brandAccountId] : undefined;
  return {
    labels: { ...DEFAULT_PORTAL_LABELS, ...(override?.labels ?? {}) },
    overviewSections: override?.overviewSections ?? DEFAULT_OVERVIEW_SECTIONS,
    metaSections: override?.metaSections ?? DEFAULT_META_SECTIONS,
    googleSections: override?.googleSections ?? DEFAULT_GOOGLE_SECTIONS,
    // ⚠️ Merged the same way `labels` is — field by field, unknown brand falls
    // through to the kyck default, never throws.
    theme: resolveBrandTheme(brandAccountId, brandName, registry),
  };
}

/**
 * ⚠️⚠️ THE GOAL-SPLIT BRAND'S LABEL OVERRIDE (round 2, 2026-09-25).
 *
 * The default block above pairs "Ad Conversions" with "Cost per lead" — a
 * contradiction on EVERY brand's report, which predates this round and is
 * deliberately NOT fixed here: changing the default would silently reword Snap's
 * and Fitstop's live reports, and fixing it fleet-wide is Scott's call. This
 * override renames it for the goal-split brand only.
 *
 * ⚠️ KEYED ON THE GOAL-SPLIT FLAG, NOT ON A BRAND ACCOUNT ID in
 * `PORTAL_BRAND_CONFIGS`, and that is a deliberate departure from the brief.
 * The goal-split report carries no brand id and no brand name anywhere in its
 * implementation (a source guard asserts it), because it is keyed on
 * `Account.portalMetaGoalReport` on the brand; a label override keyed on a
 * hardcoded id would be the one place that broke that, and would silently miss
 * a second brand given the same report. The flag IS this brand's override.
 *
 * ⚠️ APPLIED ON THE OVERVIEW AND GOOGLE PAGES ONLY. The Meta page draws its own
 * goal vocabulary, and its Lead Gen panel's "Cost per lead" is CORRECT — the
 * metric there genuinely is leads — so this must never become a find-and-replace
 * of "lead" across the report.
 */
export const GOAL_SPLIT_LABEL_OVERRIDE: Partial<PortalReportLabels> = {
  costPerLead: 'Cost per conversion',
};

/** The labels a page renders with — the brand's, plus the goal-split override when it applies. */
export function goalSplitLabels(labels: PortalReportLabels, goalSplit: boolean): PortalReportLabels {
  return goalSplit ? { ...labels, ...GOAL_SPLIT_LABEL_OVERRIDE } : labels;
}

/**
 * The label for a metric. Components call this rather than reading a field, so
 * a metric added later has one place to be named.
 */
export function metricLabel(
  metric: 'leads' | 'costPerLead' | 'spend',
  labels: PortalReportLabels,
): string {
  switch (metric) {
    case 'leads':
      return labels.leads;
    case 'costPerLead':
      return labels.costPerLead;
    case 'spend':
      return labels.spend;
  }
}

/** "Internet leads" unless the brand says otherwise (Anytime Fitness: "Digital leads"). */
export function digitalLeadsLabel(l: Partial<Pick<PortalReportLabels, 'digitalLeads'>>): string {
  return l.digitalLeads ?? 'Internet leads';
}

/** "Cost per internet lead" unless the brand says otherwise. */
export function costPerDigitalLeadLabel(l: Partial<Pick<PortalReportLabels, 'costPerDigitalLead'>>): string {
  return l.costPerDigitalLead ?? 'Cost per internet lead';
}
